import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Button from '../components/Button';
import Card from '../components/Card';
import SectionTitle from '../components/SectionTitle';
import sessionStore from '../services/sessionStore';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load dashboard data (stored locally in this browser)
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);

        // Load summary and recent sessions in parallel
        const [summaryResponse, sessionsResponse] = await Promise.all([
          sessionStore.getSessionSummary(),
          sessionStore.getSessionHistory({ limit: 5, offset: 0 }) // Get 5 most recent sessions
        ]);

        if (summaryResponse.success) {
          setSummary(summaryResponse.data);
        }

        if (sessionsResponse.success) {
          setRecentSessions(sessionsResponse.data || []);
        }

      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // Handle session continuation
  const handleContinueSession = async (sessionId) => {
    try {
      const response = await sessionStore.continueSession(sessionId);
      if (response.success) {
        navigate('/priorv-chat', {
          state: {
            sessionId,
            formData: response.data.formData,
            messages: response.data.messages,
            continuing: true
          }
        });
      } else {
        alert(response.message || 'Failed to continue session');
      }
    } catch (error) {
      console.error('Error continuing session:', error);
      alert('Failed to continue session. Please try again.');
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="page">
      <Header />

      {/* Dashboard Hero Section */}
      <section className="hero dashboard-hero" style={{
        background: `linear-gradient(135deg, rgba(52, 152, 219, 0.85) 0%, rgba(39, 174, 96, 0.85) 100%)`,
        color: 'white'
      }}>
        <div className="hero-content">
          <h1 className="hero-title">Welcome!</h1>
          <p className="hero-description">Your healthcare journey starts here — all your data stays on this device</p>
        </div>
      </section>

      {/* Main Dashboard Content */}
      <main className="container section">
        <div className="dashboard-content">
          <SectionTitle
            title="Your Dashboard"
            subtitle="Manage your healthcare preparation and reports"
          />

          {/* Summary Statistics */}
          {summary && (
            <div className="mb-8">
              <SectionTitle
                title="Your Activity"
                subtitle="Overview of your healthcare preparation sessions"
              />
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="summary-card text-center">
                  <div className="text-3xl font-bold text-primary">{summary.total_sessions || 0}</div>
                  <div className="text-sm text-gray-600">Total Sessions</div>
                </Card>
                <Card className="summary-card text-center">
                  <div className="text-3xl font-bold text-green-600">{summary.active_sessions || 0}</div>
                  <div className="text-sm text-gray-600">Active Sessions</div>
                </Card>
                <Card className="summary-card text-center">
                  <div className="text-3xl font-bold text-blue-600">{summary.completed_sessions || 0}</div>
                  <div className="text-sm text-gray-600">Completed</div>
                </Card>
                <Card className="summary-card text-center">
                  <div className="text-3xl font-bold text-purple-600">{summary.recent_sessions || 0}</div>
                  <div className="text-sm text-gray-600">This Week</div>
                </Card>
              </div>
            </div>
          )}

          <div className="dashboard-actions">
            <div className="dashboard-cards-grid">
              <Card className="dashboard-card animate-fade-in-up delay-100">
                <div className="card-icon">📋</div>
                <h3>Prepare for Your Next Doctor Visit</h3>
                <p>Fill out our comprehensive form to get personalized insights and a report to bring to your doctor.</p>
                <div className="mt-4">
                  <Button
                    variant="primary"
                    size="lg"
                    to="/priorv-form"
                    className="hover-scale"
                  >
                    Start New Session
                  </Button>
                </div>
              </Card>

              <Card className="dashboard-card animate-fade-in-up delay-200">
                <div className="card-icon">💬</div>
                <h3>Session History</h3>
                <p>View and continue your previous doctor visit preparation sessions.</p>
                <div className="mt-4">
                  <Button
                    variant="primary"
                    size="lg"
                    to="/sessions"
                    className="hover-scale"
                  >
                    View Sessions
                  </Button>
                </div>
              </Card>

            </div>
          </div>

          {/* Recent Sessions Section */}
          <div className="mt-8">
            <div className="flex justify-between items-center mb-6">
              <SectionTitle
                title="Recent Sessions"
                subtitle="Your latest doctor visit preparation sessions"
              />
              {recentSessions.length > 0 && (
                <Button variant="secondary" to="/sessions">
                  View All Sessions
                </Button>
              )}
            </div>

            {loading ? (
              <div className="loading-indicator text-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-gray-500">Loading sessions...</p>
              </div>
            ) : recentSessions.length > 0 ? (
              <div className="space-y-4">
                {recentSessions.map(session => (
                  <Card key={session.id} className="session-card">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="text-lg font-semibold text-gray-900">
                            {session.chief_complaint || 'General Visit Preparation'}
                          </h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                            {session.status}
                          </span>
                        </div>

                        <div className="text-sm text-gray-600 space-y-1">
                          <p><strong>Created:</strong> {formatDate(session.created_at)}</p>
                          <p><strong>Messages:</strong> {session.message_count || 0}</p>
                        </div>
                      </div>

                      <div className="flex gap-2 ml-4">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleContinueSession(session.session_id)}
                        >
                          Continue
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="empty-state text-center p-8">
                <div className="text-gray-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.959 8.959 0 01-4.906-1.405L3 21l2.595-5.094A8.959 8.959 0 013 12c0-4.418 3.582-8 8-8s8 3.582 8 8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No sessions yet</h3>
                <p className="text-gray-500 mb-4">Start by preparing for your next doctor visit!</p>
                <Button
                  variant="primary"
                  to="/priorv-form"
                >
                  Start Your First Session
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DashboardPage;
